import axios from 'axios';
import { fetchTransactions } from '../Transaction/TransactionActions';
import { fetchOrganisations } from '../../../Organisation/OrganisationAction';
import { fetchAllPlans } from '../../../Dashboard/scenes/Plan/PlanAction';
import { fetchUser } from '../User/UserActions';
import { brokerageIdSelector } from './BrokerSelectors';
import { userIdSelector } from '../User/UserSelectors';
import {
  downloadOnboardingUsersImportTemplate,
  importOrgUsers,
  stageManualOrgUserInvitee,
  sendOnboardingInvitations,
  updateOrgUserInvitee as updateOrgUserInviteeRequest,
  deleteOrgUserInvitee as deleteOrgUserInviteeRequest,
  resendBrokerOnboardingInvitation,
} from '../../../Onboarding/onboardingApi';
import getAvailableSubscriptionKeys from '../Subscription/subscriptionApi';

const API_BASE = process.env.REACT_APP_API_BASE;

export const types = {
  REQUEST_BROKER_ORGS: 'broker/REQUEST_BROKER_ORGS',
  REQUEST_BROKER: 'broker/REQUEST_BROKER',
  RECEIVE_BROKER: 'broker/RECEIVE_BROKER',
  REQUEST_BROKERS: 'broker/REQUEST_BROKERS',
  RECEIVE_BROKERS: 'broker/RECEIVE_BROKERS',
  REQUEST_BROKERAGE: 'broker/REQUEST_BROKERAGE',
  RECEIVE_BROKERAGE: 'broker/RECEIVE_BROKERAGE',
  REQUEST_BROKERAGE_SUBSCRIPTIONS: 'broker/REQUEST_BROKERAGE_SUBSCRIPTIONS',
  RECEIVE_BROKERAGE_SUBSCRIPTIONS: 'broker/RECEIVE_BROKERAGE_SUBSCRIPTIONS',
};

const requestBrokerOrgs = (bool) => ({ type: types.REQUEST_BROKER_ORGS, requestBrokerOrgs: bool });

const requestBroker = () => ({ type: types.REQUEST_BROKER });
const receiveBroker = (data) => ({ type: types.RECEIVE_BROKER, brokerInfo: data });

const requestBrokers = () => ({ type: types.REQUEST_BROKERS });
const receiveBrokers = (data) => ({ type: types.RECEIVE_BROKERS, brokers: data });

function finishBrokersLoading(getState, dispatch) {
  dispatch(receiveBrokers(getState().brokerReducer.brokers));
}

const requestBrokerageSubscriptions = () => ({ type: types.REQUEST_BROKERAGE_SUBSCRIPTIONS });
const receiveBrokerageSubscriptions = (data) => ({
  type: types.RECEIVE_BROKERAGE_SUBSCRIPTIONS,
  brokerageSubscriptions: data,
});

function finishBrokerageSubscriptionsLoading(getState, dispatch) {
  dispatch(receiveBrokerageSubscriptions(getState().brokerReducer.brokerageSubscriptions));
}

const requestBrokerage = () => ({ type: types.REQUEST_BROKERAGE });
const receiveBrokerage = (data) => ({ type: types.RECEIVE_BROKERAGE, brokerageInfo: data });

export const createOrgFromBroker = (data) => async (dispatch, getState) => {
  dispatch(requestBrokerOrgs(true));
  const brokerageId = brokerageIdSelector(getState());
  const url = `${API_BASE}brokerages/${brokerageId}/organisations`;
  try {
    const res = await axios.post(url, data);
    dispatch(fetchOrganisations());
    dispatch(fetchAllPlans());
    dispatch(fetchUser());
    return Promise.resolve(res);
  } catch (err) {
    return Promise.reject(err);
  } finally {
    dispatch(requestBrokerOrgs(false));
  }
};

export const fetchBroker = () => async (dispatch) => {
  dispatch(requestBroker());
  const url = `${API_BASE}user/broker`;
  try {
    const res = await axios.get(url);
    dispatch(receiveBroker(res.data));
    return res;
  } catch (err) {
    dispatch(receiveBroker(null));
    return null;
  }
};

export const fetchBrokers = (brokerageId) => async (dispatch, getState) => {
  dispatch(requestBrokers());
  const url = `${API_BASE}brokerages/${brokerageId}/brokers`;
  try {
    const res = await axios.get(url);
    dispatch(receiveBrokers(res.data));
  } catch (err) {
    finishBrokersLoading(getState, dispatch);
  }
};

export const fetchBrokerageSubscriptions = (brokerageId, { silent = false } = {}) => async (dispatch) => {
  if (!silent) {
    dispatch(requestBrokerageSubscriptions());
  }
  const subscriptionsUrl = `${API_BASE}brokerages/${brokerageId}/subscriptions`;

  try {
    const [subscriptionsRes, availableKeysRes] = await Promise.all([
      axios.get(subscriptionsUrl),
      getAvailableSubscriptionKeys(brokerageId),
    ]);

    dispatch(
      receiveBrokerageSubscriptions({
        ...subscriptionsRes.data,
        availableKeys: Array.isArray(availableKeysRes.data) ? availableKeysRes.data : [],
      }),
    );
    return subscriptionsRes.data;
  } catch (err) {
    if (!silent) {
      dispatch(receiveBrokerageSubscriptions());
    }
    return Promise.reject(err);
  }
};

export const createBroker = (brokerageId, data) => async (dispatch, getState) => {
  dispatch(requestBrokers());
  const url = `${API_BASE}brokerages/${brokerageId}/brokers`;
  try {
    const res = await axios.post(url, data);
    dispatch(fetchBrokers(brokerageId));
    return Promise.resolve(res);
  } catch (err) {
    finishBrokersLoading(getState, dispatch);
    return Promise.reject(err);
  }
};

export const updateBroker = (brokerId, data) => async (dispatch, getState) => {
  dispatch(requestBrokers());
  const url = `${API_BASE}brokers/${brokerId}`;
  try {
    const res = await axios.patch(url, data);
    const brokerageId = brokerageIdSelector(getState());
    dispatch(fetchBrokers(brokerageId));
    return Promise.resolve(res);
  } catch (err) {
    finishBrokersLoading(getState, dispatch);
    return Promise.reject(err);
  }
};

export const deleteBroker = (brokerId) => async (dispatch, getState) => {
  dispatch(requestBrokers());
  const url = `${API_BASE}brokers/${brokerId}`;
  try {
    const res = await axios.delete(url);
    const brokerageId = brokerageIdSelector(getState());
    dispatch(fetchBrokers(brokerageId));
    return Promise.resolve(res);
  } catch (err) {
    finishBrokersLoading(getState, dispatch);
    return Promise.reject(err);
  }
};

/** POST /v{version}/onboarding/broker/resend-invitation */
export const resendBrokerInvitation = (broker) => async (dispatch, getState) => {
  const brokerageId = broker?.brokerageId ?? brokerageIdSelector(getState());
  try {
    const res = await resendBrokerOnboardingInvitation({
      brokerageId,
      email: broker?.email,
    });
    dispatch(fetchBrokers(brokerageId));
    return Promise.resolve(res);
  } catch (err) {
    return Promise.reject(err);
  }
};

export const fetchBrokerage = (brokerageId) => async (dispatch) => {
  dispatch(requestBrokerage());
  const url = `${API_BASE}brokerages/${brokerageId}`;
  try {
    const res = await axios.get(url);
    dispatch(receiveBrokerage(res.data));
  } catch (err) {
    dispatch(receiveBrokerage());
  }
};

export const transferQuota = (brokerageId, data) => async (dispatch, getState) => {
  dispatch(requestBrokerage());
  const url = `${API_BASE}brokerages/transfer`;
  try {
    const res = await axios.post(url, {
      fromBrokerageId: Number(brokerageId),
      toBrokerageId: Number(data.toBrokerageId),
      amount: Number(data.amount),
      initiator: userIdSelector(getState()),
    });
    dispatch(fetchBrokerage(brokerageId));
    dispatch(fetchTransactions());
    return Promise.resolve(res);
  } catch (err) {
    dispatch(receiveBrokerage());
    return Promise.reject(err);
  }
};

export const updateBrokerage = (brokerageId, data) => async (dispatch) => {
  dispatch(requestBrokerage());
  const url = `${API_BASE}brokerages/${brokerageId}`;
  try {
    const res = await axios.patch(url, data);
    dispatch(fetchBrokerage(brokerageId));
    return Promise.resolve(res);
  } catch (err) {
    dispatch(receiveBrokerage());
    return Promise.reject(err);
  }
};

export const acceptBrokerageInvitation = (brokerageId, data) => () => {
  const url = `${API_BASE}brokerages/${brokerageId}/invitees/accept`;
  return axios.post(url, data);
};

export const createPromoCode = (data) => async (dispatch, getState) => {
  dispatch(requestBrokerageSubscriptions());
  const url = `${API_BASE}promo-codes/create`;
  const brokerageId = data?.brokerageId ?? data?.BrokerageId ?? brokerageIdSelector(getState());
  if (!brokerageId) {
    return Promise.reject(new Error('Brokerage id is required to create a promo code.'));
  }

  const body = {
    brokerageId: Number(brokerageId),
    quantity: Number(data.quantity),
    code: String(data.code ?? '').trim(),
  };
  if (data.expiryDate) {
    body.expiryDate = data.expiryDate;
  }
  try {
    const res = await axios.post(url, body);
    const brokerageId = brokerageIdSelector(getState());
    dispatch(fetchBrokerageSubscriptions(brokerageId));
    dispatch(fetchBrokerage(brokerageId));
    return Promise.resolve(res);
  } catch (err) {
    finishBrokerageSubscriptionsLoading(getState, dispatch);
    return Promise.reject(err);
  }
};

export const revokePromoCode = (data) => async (dispatch, getState) => {
  dispatch(requestBrokerageSubscriptions());
  const url = `${API_BASE}promo-codes/revoke`;
  try {
    const res = await axios.post(url, data);
    const brokerageId = brokerageIdSelector(getState());
    dispatch(fetchBrokerageSubscriptions(brokerageId));
    dispatch(fetchBrokerage(brokerageId));
    return Promise.resolve(res);
  } catch (err) {
    finishBrokerageSubscriptionsLoading(getState, dispatch);
    return Promise.reject(err);
  }
};

/** POST /v{version}/promo-codes/Reinstate — restore a revoked promo code */
export const reinstatePromoCode = (data) => async (dispatch, getState) => {
  const promoCode = data?.promoCode ?? data?.code;
  if (!promoCode) {
    return Promise.reject(new Error('Promo code is required to reinstate this promo.'));
  }

  dispatch(requestBrokerageSubscriptions());
  const url = `${API_BASE}promo-codes/Reinstate`;

  try {
    const res = await axios.post(url, { promoCode });
    const brokerageId = brokerageIdSelector(getState());
    dispatch(fetchBrokerageSubscriptions(brokerageId));
    dispatch(fetchBrokerage(brokerageId));
    return Promise.resolve(res);
  } catch (err) {
    finishBrokerageSubscriptionsLoading(getState, dispatch);
    return Promise.reject(err);
  }
};

function getPromoCodeId(data) {
  return data?.promoCodeId ?? data?.promoCodeid ?? data?.id ?? null;
}

function getUnusedPromoCount(data) {
  return Number(data?.count ?? data?.unAssigned ?? data?.unassigned ?? data?.remaining ?? 0) || 0;
}

/** POST /v{version}/promo-codes/revert-unused-promocode */
export const revertUnusedPromoCodes = (data) => async (dispatch, getState) => {
  const items = (Array.isArray(data) ? data : [data]).filter(Boolean);
  const payload = items
    .map((item) => ({
      promoCodeid: getPromoCodeId(item),
      count: getUnusedPromoCount(item),
    }))
    .filter((item) => item.promoCodeid && item.count > 0);

  if (!payload.length) {
    return Promise.reject(new Error('No unused promo keys to cancel.'));
  }

  dispatch(requestBrokerageSubscriptions());
  const url = `${API_BASE}promo-codes/revert-unused-promocode`;
  const brokerageId = brokerageIdSelector(getState());
  try {
    const responses = await Promise.all(payload.map((body) => axios.post(url, body)));
    const lastRes = responses[responses.length - 1];
    if (brokerageId) {
      Promise.resolve(dispatch(fetchBrokerageSubscriptions(brokerageId))).catch(() => undefined);
      Promise.resolve(dispatch(fetchBrokerage(brokerageId))).catch(() => undefined);
    }
    return Promise.resolve(lastRes);
  } catch (err) {
    if (brokerageId) {
      Promise.resolve(dispatch(fetchBrokerageSubscriptions(brokerageId))).catch(() => undefined);
      Promise.resolve(dispatch(fetchBrokerage(brokerageId))).catch(() => undefined);
    } else {
      finishBrokerageSubscriptionsLoading(getState, dispatch);
    }
    return Promise.reject(err);
  }
};

export const assignUser = (data) => async (dispatch, getState) => {
  dispatch(requestBrokerageSubscriptions());
  const url = `${API_BASE}subscriptions/assign`;
  try {
    const res = await axios.post(url, data);
    const brokerageId = brokerageIdSelector(getState());
    dispatch(fetchBrokerageSubscriptions(brokerageId));
    dispatch(fetchBroker());
    dispatch(fetchBrokerage(brokerageId));
    return Promise.resolve(res);
  } catch (err) {
    finishBrokerageSubscriptionsLoading(getState, dispatch);
    return Promise.reject(err);
  }
};

/** Send staged invites and re-send via POST onboarding/orgusers/send-invitations. */
export const sendPendingInvites = ({ uploadIds = [] } = {}) => async () => {
  if (!uploadIds.length) return null;
  return sendOnboardingInvitations({ uploadIds });
};

export const revokeSubscriptionKey = (data) => async (dispatch, getState) => {
  dispatch(requestBrokerageSubscriptions());
  const url = `${API_BASE}subscriptions/revoke`;
  try {
    const res = await axios.post(url, data);
    const brokerageId = brokerageIdSelector(getState());
    dispatch(fetchBrokerageSubscriptions(brokerageId));
    dispatch(fetchBroker());
    return Promise.resolve(res);
  } catch (err) {
    finishBrokerageSubscriptionsLoading(getState, dispatch);
    return Promise.reject(err);
  }
};

/** POST /v{version}/subscriptions/renew — broker/admin renew using an available subscription key */
export const renewSubscription = (data) => async (dispatch, getState) => {
  const subscriptionId = data?.subscriptionId ?? data?.SubscriptionId;
  if (!subscriptionId) {
    return Promise.reject(new Error('Subscription id is required to renew this subscription.'));
  }

  dispatch(requestBrokerageSubscriptions());
  const url = `${API_BASE}subscriptions/renew`;

  try {
    const res = await axios.post(url, { subscriptionId });
    const brokerageId = brokerageIdSelector(getState());
    dispatch(fetchBrokerageSubscriptions(brokerageId));
    dispatch(fetchBroker());
    return Promise.resolve(res);
  } catch (err) {
    finishBrokerageSubscriptionsLoading(getState, dispatch);
    return Promise.reject(err);
  }
};

/** POST /v{version}/subscriptions/Reinstate — restore a revoked subscription */
export const reinstateSubscription = (data) => async (dispatch, getState) => {
  const subscriptionId = data?.subscriptionId ?? data?.SubscriptionId;
  if (!subscriptionId) {
    return Promise.reject(new Error('Subscription id is required to reinstate this subscription.'));
  }

  dispatch(requestBrokerageSubscriptions());
  const url = `${API_BASE}subscriptions/Reinstate`;

  try {
    const res = await axios.post(url, { subscriptionId });
    const brokerageId = brokerageIdSelector(getState());
    dispatch(fetchBrokerageSubscriptions(brokerageId));
    dispatch(fetchBroker());
    return Promise.resolve(res);
  } catch (err) {
    finishBrokerageSubscriptionsLoading(getState, dispatch);
    return Promise.reject(err);
  }
};

/** GET /v{version}/onboarding/broker/export-template */
export const downloadImportTemplate = () => downloadOnboardingUsersImportTemplate();

/** POST /v{version}/onboarding/orgusers/import — loads users only; invite from Assign tab. */
export const importSubscriptions = (brokerageId, formData) => async (dispatch) => {
  try {
    const importResponse = await importOrgUsers(formData, { brokerageId });
    dispatch(fetchBrokerageSubscriptions(brokerageId));
    return Promise.resolve(importResponse);
  } catch (err) {
    return Promise.reject(err);
  }
};

/** POST /v{version}/onboarding/orgusers/invitees — stage user (no email). */
export const createOrgUserInvitee = (data) => async (dispatch, getState) => {
  const brokerageId = brokerageIdSelector(getState());

  try {
    const result = await stageManualOrgUserInvitee({ ...data, brokerageId });
    const current = getState().brokerReducer.brokerageSubscriptions;
    dispatch(
      receiveBrokerageSubscriptions({
        ...result.subscriptionsData,
        availableKeys: current?.availableKeys ?? [],
      }),
    );
    return Promise.resolve(result);
  } catch (err) {
    return Promise.reject(err);
  }
};

/** POST /v{version}/onboarding/orgusers/send-invitations — staged users and re-sends. */
export const sendOnboardingUserInvitations = (params) => sendPendingInvites(params);

/** PUT /v{version}/onboarding/orgusers/invitees/{id} */
export const updateOrgUserInvitee = (id, body) => async (dispatch, getState) => {
  try {
    const res = await updateOrgUserInviteeRequest(id, body);
    const brokerageId = brokerageIdSelector(getState());
    dispatch(fetchBrokerageSubscriptions(brokerageId));
    return Promise.resolve(res);
  } catch (err) {
    return Promise.reject(err);
  }
};

/** DELETE /v{version}/onboarding/orgusers/invitees/{id} */
export const deleteOrgUserInvitee = (id) => async (dispatch, getState) => {
  try {
    const res = await deleteOrgUserInviteeRequest(id);
    const brokerageId = brokerageIdSelector(getState());
    await dispatch(fetchBrokerageSubscriptions(brokerageId, { silent: true }));
    return Promise.resolve(res);
  } catch (err) {
    return Promise.reject(err);
  }
};

// API not yet implemented — endpoint: POST subscriptions/bulk-assign
export const bulkAssignPromoCode = (data) => async (dispatch, getState) => {
  const url = `${API_BASE}subscriptions/bulk-assign`;
  try {
    const res = await axios.post(url, data);
    const brokerageId = brokerageIdSelector(getState());
    dispatch(fetchBrokerageSubscriptions(brokerageId));
    return Promise.resolve(res);
  } catch (err) {
    return Promise.reject(err);
  }
};
